import { ApiError } from '../../lib/api';
import { getIdToken } from '../../lib/firebase';
import type { UploadedLogo } from './types';

const MAX_BYTES = 2 * 1024 * 1024;
const ACCEPTED = ['image/png', 'image/jpeg', 'image/svg+xml'];

/** Valida en el navegador lo mismo que `POST /v1/logos` para avisar antes de subir. */
export function logoProblem(file: File): string | null {
  if (!ACCEPTED.includes(file.type)) return 'Sube el logo en PNG, JPG o SVG.';
  if (file.size > MAX_BYTES) return 'El logo supera 2 MB. Sube una versión más liviana.';
  return null;
}

/** `POST /v1/logos` en multipart: el cliente JSON de `lib/api` no envía archivos. */
export async function uploadLogo(
  file: File,
  orgId: string | undefined,
  deps: { fetchImpl?: typeof fetch; token?: () => Promise<string | null>; baseUrl?: string } = {},
): Promise<UploadedLogo> {
  const baseUrl = (deps.baseUrl ?? import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
  const token = await (deps.token ?? (() => getIdToken()))();
  const form = new FormData();
  form.append('file', file, file.name);
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (orgId) headers['X-Org-Id'] = orgId;
  let response: Response;
  try {
    response = await (deps.fetchImpl ?? fetch)(`${baseUrl}/v1/logos`, { method: 'POST', headers, body: form });
  } catch (err) {
    throw new ApiError({ kind: 'network', status: 0, message: 'No pudimos subir el logo. Revisa tu conexión e intenta de nuevo.', details: err });
  }
  const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) {
    throw new ApiError({
      kind: response.status === 413 || response.status === 415 || response.status === 422 ? 'validation' : response.status === 403 ? 'forbidden' : 'server',
      status: response.status,
      code: typeof payload.code === 'string' ? payload.code : undefined,
      message: typeof payload.error === 'string' ? payload.error : 'No pudimos subir el logo.',
    });
  }
  return payload as unknown as UploadedLogo;
}
